import * as utils from '../functions/utils';
import eventDetail from './eventDetail';
import * as messageUtils from '../functions/messageUtils';
import { APPROVAL_STATUS, MEMBER_TYPES } from '../constants/constants';
import {
  ITravel,
  ITravelPlan,
  ITravelPlanFull,
  Travel,
  TravelDestinationItem,
  TravelPlanData,
  TravelProfile,
  UserProfile,
} from 'redhotpie-shared-lib/interfaces/ITravelPlan';
import { IUserLoginInfo } from 'redhotpie-shared-lib/interfaces/IUser';
import Photo from './photo';
import User from './user';
import { IFriend } from 'redhotpie-shared-lib/interfaces/IFriendUser';

export default class TravelPlan {
  dateArrived: string;
  dateDeparture: string;
  description: string;
  locationText: string;

  constructor() {
    this.dateArrived = '';
    this.dateDeparture = '';
    this.description = '';
    this.locationText = '';
  }

  static myTravelPlanMappingObject(_travelPlan: TravelPlanData): ITravelPlanFull {
    const objT = {
      dateArrived: '',
      dateDeparture: '',
      description: '',
      isHidden: false,
      locationGID: '',
      locationLabel: '',
      locationText: '',
      showSimpleDates: false,
      textApprovalStatus: '',
      userTravelProfileID: 0,
      approvalStatus: undefined as string | undefined,
    };
    objT.dateArrived = utils.extractDateToUnix(_travelPlan.DateArrived);
    objT.dateDeparture = utils.extractDateToUnix(_travelPlan.DateDeparture);
    objT.description = _travelPlan.Description;
    objT.isHidden = _travelPlan.IsHidden;
    objT.locationText = _travelPlan.LocationText;
    objT.locationGID = _travelPlan.LocationGID;
    objT.locationLabel = _travelPlan.LocationLabel;
    objT.showSimpleDates = _travelPlan.ShowSimpleDates;
    objT.textApprovalStatus = _travelPlan.TextApprovalStatus;
    objT.userTravelProfileID = _travelPlan.UserTravelProfileID;
    objT.approvalStatus =
      objT.textApprovalStatus === APPROVAL_STATUS.REJECTED
        ? objT.textApprovalStatus
        : objT.textApprovalStatus === APPROVAL_STATUS.LOCKED
          ? APPROVAL_STATUS.PENDING
          : undefined; // Field using only on ReactJS
    return objT;
  }

  static mappingObject(_travelPlan: TravelPlanData): ITravelPlan {
    if (_travelPlan) {
      let travelPlan = Object.assign(new TravelPlan(), _travelPlan);
      travelPlan.dateArrived = utils.extractDateToUnix(_travelPlan.DateArrived);
      travelPlan.dateDeparture = utils.extractDateToUnix(_travelPlan.DateDeparture);
      travelPlan.description = _travelPlan.Description;
      travelPlan.locationText = _travelPlan.LocationText;
      travelPlan = utils.cleanObjectKeys(Object.keys(new TravelPlan()), travelPlan); // clean other key doesn't exist in user model
      return travelPlan;
    }
    return new TravelPlan();
  }

  static mappingObjects(_travelPlans: TravelPlanData[]): ITravelPlan[] {
    const travelPlans: ITravelPlan[] = [];
    _travelPlans.forEach(_travelPlan => {
      const travelPlan = TravelPlan.mappingObject(_travelPlan);
      travelPlans.push(travelPlan);
    });
    return travelPlans;
  }

  static mappingMyTravelObjects(_travelPlans: TravelPlanData[]): ITravelPlanFull[] {
    const travelPlans: ITravelPlanFull[] = [];
    _travelPlans.forEach(_travelPlan => {
      const travelPlan = TravelPlan.myTravelPlanMappingObject(_travelPlan);
      travelPlans.push(travelPlan);
    });
    return travelPlans;
  }

  static mappingProfile(user: UserProfile): IFriend {
    let obj = Object.assign(new User(), user);
    obj.userId = user.MemberID ? user.MemberID : -1;
    obj.xmppId = messageUtils.removePrefixAndSuffix(user.MemberID);
    obj.userName = user.UserName ? user.UserName : '';
    obj.memberType = user.MemberType ? user.MemberType : MEMBER_TYPES.GUEST;
    obj.memberTypeTitle = user.MemberTypeTitle ? eventDetail.mappingMemberTypeTitle(user.MemberTypeTitle) : '';
    obj.age1 = user.Age1 ? user.Age1 : -1;
    obj.age2 = user.Age2 ? user.Age2 : -1;
    obj.ageTitle = user.AgeTitle ? user.AgeTitle : '';
    obj.location = user.Location ? user.Location : '';
    obj.mainPhoto = user.MainPhoto ? Photo.mappingObject(user.MainPhoto) : {};
    obj.isOnline = user.IsOnline ? user.IsOnline : false;
    obj.memberLevel = user.MemberLevel ? user.MemberLevel : '';
    obj.isDiamond = user.IsDiamond ? user.IsDiamond : false;
    obj.countryId = user.CountryID ? user.CountryID : -1;
    obj.userType = user.UserType || '';
    obj.isViewable = utils.hasOwnProperty(user, 'IsViewable') ? user.IsViewable : true;

    return obj as unknown as IFriend;
  }

  static mappingProfiles(_data: UserProfile[]) {
    const data: any[] = [];
    _data.forEach(profile => {
      const _profile = TravelPlan.mappingProfile(profile);
      data.push(_profile);
    });
    return data;
  }

  static mappingTravelObject(_travel: Travel): ITravel {
    const travel: any = {};
    if (_travel) {
      travel.city = _travel.City;
      travel.count = _travel.Count;
      travel.country = _travel.Country;
      travel.googleMapPlaceID = _travel.GooglePlaceID;
      travel.id = _travel.ID;
      travel.locationType = _travel.LocationType;
      travel.profiles = _travel.Profiles && _travel.Profiles.length > 0 ? TravelPlan.mappingProfiles(_travel.Profiles) : [];
      travel.bannerUrl = _travel.BannerUrl;
      travel.state = _travel.State;
    }
    return travel;
  }

  static mappingTravelObjects(_travel: Travel[]): ITravel[] {
    const data: ITravel[] = [];
    _travel.forEach(i => {
      const _travel = TravelPlan.mappingTravelObject(i);
      data.push(_travel);
    });
    return data;
  }

  static mappingTravelProfile(_travelProfile: TravelProfile): ITravelPlan {
    const travelProfile: ITravelPlan = {
      dateArrived: '',
      dateDeparture: '',
      description: '',
      destination: '',
      showSimpleDates: false,
      locationText: '',
    };
    if (_travelProfile) {
      travelProfile.dateArrived = _travelProfile.DateArrived;
      travelProfile.dateDeparture = _travelProfile.DateDeparture;
      travelProfile.description = _travelProfile.Description;
      travelProfile.destination = _travelProfile.Destination;
      travelProfile.showSimpleDates = _travelProfile.ShowSimpleDates;
    }
    return travelProfile;
  }

  static mappingTravelDestinationItem(_obj: TravelDestinationItem): ITravelPlanFull {
    const obj: any = {};
    if (_obj && _obj.Profile && _obj.TravelProfile) {
      obj.profile = TravelPlan.mappingProfile(_obj.Profile);
      obj.dateArrived = _obj.TravelProfile.DateArrived;
      obj.dateDeparture = _obj.TravelProfile.DateDeparture;
      obj.description = _obj.TravelProfile.Description;
      obj.locationText = _obj.TravelProfile.Destination;
      obj.showSimpleDates = _obj.TravelProfile.ShowSimpleDates;
      obj.userTravelProfileID = _obj.TravelProfile.UserTravelProfileID;
      obj.locationGID = _obj.TravelProfile.LocationGID;
      //  obj.travelProfile = TravelPlan.mappingTravelProfile(_obj.TravelProfile)
    }
    return obj as ITravelPlanFull;
  }

  static mappingTravelDestination(_data: TravelDestinationItem[]) {
    const data: any[] = [];
    _data.forEach(i => {
      const obj = TravelPlan.mappingTravelDestinationItem(i);
      data.push(obj);
    });
    return data;
  }
}
